import { gql, subscribe } from '@polleverywhere/graphql-client'
import toSnakeCase from '../../../util/toSnakeCase'

/**
 * Archive all results that were created before the provided date
 *
 * @this Discourse
 * @param {string} before Timestamp (Date parseable) to compare results against
 */
function onActionArchive (before) {
  const compareTo = new Date(before).getTime()
  const resultsToMaintain = this._results.filter((result) => {
    const created = new Date(result.get('created_at'))
    return compareTo < created
  })
  this._results.set(resultsToMaintain)

  const myResultsToMaintain = this._myResults.filter((result) => {
    const created = new Date(result.get('created_at'))
    return compareTo < created
  })
  this.myResults.set(myResultsToMaintain)
}

function logSubscriptionError (response) {
  console.warn('GraphQL subscription error', response.error)
  window.Rollbar?.warning('GraphQL subscription error', response.error)
}

/**
 * @typedef {Object} SubscriptionDefinition
 * @property {string} queryName Name of query
 * @property {(this: Discourse, data: object) => void} onData Handler for the returned data (after extraction and case fixing)
 * @property {(model: Discourse) => object} params Query definition to pass to our graphql-client
 */

/**
 * @type {SubscriptionDefinition[]}
 */
export const SUBSCRIPTIONS = [
  {
    queryName: 'participantsDiscourseResultCreated',
    params: function (model) {
      return {
        query: gql`
          subscription participantsDiscourseResultCreated($participantKey: ID!) {
            participantsDiscourseResultCreated(participantKey: $participantKey) {
              id,
              value,
              createdAt,
              participantIdentifier,
              votes {
                netVotes
              }
            }
          }
        `,
        variables: { participantKey: model.get('participant_key') }
      }
    },
    onData: function (result) {
      // API nests a 'votes' object, but we need it on the root
      if (result.votes) {
        result.net_votes = result.votes.net_votes
        delete result.votes
      }

      // Conduit doesn't publish `approved`, but if it does publish it then it's definately not hidden
      // this works around a result which is auto-censored (`approved: false` when we submit it) but
      // then gets shown by the presenter explicitly [note:20200210@1435]
      result.approved = null

      this.results.add(result, { merge: true })
    }
  },
  {
    queryName: 'participantsDiscourseResultDeleted',
    params: function (model) {
      return {
        query: gql`
          subscription participantsDiscourseResultDeleted($participantKey: ID!) {
            participantsDiscourseResultDeleted(participantKey: $participantKey) {
              id
            }
          }
        `,
        variables: { participantKey: model.get('participant_key') }
      }
    },
    onData: function (result) {
      this.results.remove(result.id)
    }
  },
  {
    queryName: 'participantsResultVotesChanged',
    params: function (model) {
      return {
        query: gql`
          subscription participantsResultVotesChanged($participantKey: ID!) {
            participantsResultVotesChanged(participantKey: $participantKey) {
              resultId,
              netVotes
            }
          }
        `,
        variables: { participantKey: model.get('participant_key') }
      }
    },
    onData: function (data) {
      const result = this.results.findWhere({ id: data.result_id })
      result?.set('net_votes', data.net_votes)
    }
  },
  {
    queryName: 'participantsDiscourseInstanceUpdated',
    params: function (model) {
      return {
        query: gql`
          subscription participantsDiscourseInstanceUpdated($participantKey: ID!) {
            participantsDiscourseInstanceUpdated(participantKey: $participantKey) {
              moderationEnabled
              state
            }
          }
        `,
        variables: { participantKey: model.get('participant_key') }
      }
    },
    onData: function (data) {
      this.set(data)
    }
  },
  {
    queryName: 'participantsDiscoursePrototypeUpdated',
    params: function (model) {
      return {
        query: gql`
          subscription participantsDiscoursePrototypeUpdated($sharedKey: ID!) {
            participantsDiscoursePrototypeUpdated(sharedKey: $sharedKey) {
              title
              registeredParticipantsOnly
              anonymous
              showParticipants
              maxVotes
            }
          }
        `,
        variables: { sharedKey: model.get('shared_key') }
      }
    },
    onData: function (data) {
      this.set(data)
    }
  },
  {
    queryName: 'participantsPollActions',
    params: function (model) {
      return {
        query: gql`
          subscription participantsPollActions($participantKey: ID!) {
            participantsPollActions(participantKey: $participantKey) {
              __typename
              ... on PollActionArchive {
                actionAt
              }
            }
          }
        `,
        variables: { participantKey: model.get('participant_key') }
      }
    },
    onData: function (data) {
      const type = data.__typename ?? data.typename // __ prefix may be dropped by snake-case (https://github.com/blakeembrey/change-case/issues/56)
      if (type === 'PollActionArchive') {
        onActionArchive.call(this, data.action_at)
      }
    }
  }
]

/**
 * @param {Discourse} model Discourse model
 * @param {SubscriptionDefinition} def Subscription definition
 * @returns {Promise<{unsubscribe: Function}>} Subscription object to allow unsubscribing
 */
export function createSubscription (model, def) {
  const dataHandler = def.onData.bind(model)
  const subscription = subscribe(def.params(model), (response) => {
    if (response.error) return logSubscriptionError(response)

    const parsedData = toSnakeCase(response.data[def.queryName])
    dataHandler(parsedData)
  })
  return subscription
}

/**
 * @param {Discourse} model Discourse model
 * @returns {Promise<{unsubscribe: Function}>[]} Subscription objects to allow unsubscribing
 */
export function createAllSubscriptions (model) {
  return SUBSCRIPTIONS.map((subscriptionDef) => createSubscription(model, subscriptionDef))
}
